/**
 * Service de calcul des heures facturables basé sur les rendez-vous réels
 * 
 * Ce service calcule automatiquement les heures à facturer aux bénéficiaires
 * en se basant sur les rendez-vous assignés ou terminés
 */

import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { getFraisMandat } from './tarificationService';

class BillingCalculationService {
  
  /**
   * Calcule les heures facturables pour un client sur une période
   * @param {string} clientId - ID du client
   * @param {Date|string} periodeDebut - Date de début de période
   * @param {Date|string} periodeFin - Date de fin de période
   * @param {Array} statuts - Statuts des rendez-vous à inclure (défaut: ['completed', 'assigne', 'libre', 'in_progress'])
   * @returns {Object} Calcul détaillé des heures facturables
   */
  async calculerHeuresFacturables(clientId, periodeDebut, periodeFin, statuts = ['completed', 'assigne', 'libre', 'in_progress']) {
    try {
      // Convertir les dates en objets Date valides
      const debut = this.convertirEnDate(periodeDebut);
      const fin = this.convertirEnDate(periodeFin);
      
      console.log(`📊 Calcul heures facturables pour client ${clientId}`, {
        periodeDebut: debut.toISOString(),
        periodeFin: fin.toISOString(),
        statuts,
        debutLocal: debut.toLocaleDateString('fr-FR'),
        finLocal: fin.toLocaleDateString('fr-FR')
      });

      // Récupérer tous les rendez-vous de la période
      const rendezVous = await this.getRendezVousClient(clientId, debut, fin, statuts);
      
      // Calculer le total d'heures et détails
      const calculDetails = rendezVous.reduce((acc, rdv) => {
        console.log(`📊 Calcul durée pour rendez-vous ${rdv.id}:`, {
          startTime: rdv.startTime,
          endTime: rdv.endTime,
          typeStart: typeof rdv.startTime,
          typeEnd: typeof rdv.endTime
        });
        
        const dureeHeures = this.calculerDureeHeures(rdv.startTime, rdv.endTime);
        
        console.log(`📊 Durée calculée: ${dureeHeures}h pour ${rdv.id}`);
        
        return {
          totalHeures: acc.totalHeures + dureeHeures,
          nombreRendezVous: acc.nombreRendezVous + 1,
          rendezVousDetails: [
            ...acc.rendezVousDetails,
            {
              id: rdv.id,
              date: rdv.date || rdv.startTime, // Date du rendez-vous (champ 'date' de Firestore)
              dateDebut: rdv.startTime,
              dateFin: rdv.endTime,
              heures: dureeHeures, // Ajout du champ 'heures' pour le PDF
              dureeHeures: dureeHeures,
              intervenant: rdv.intervenantNom || rdv.employeeName,
              type: rdv.typePrestation || 'intervention',
              statut: rdv.statut,
              status: rdv.status || rdv.statut, // Ajout du champ 'status' pour le PDF
              clientId: rdv.clientId,
              notes: rdv.notes || rdv.description || '' // Ajout des notes si disponibles
            }
          ]
        };
      }, { totalHeures: 0, nombreRendezVous: 0, rendezVousDetails: [] });

      console.log(`📊 Résultat calcul final:`, {
        totalHeures: calculDetails.totalHeures,
        nombreRendezVous: calculDetails.nombreRendezVous,
        estNaN: isNaN(calculDetails.totalHeures)
      });

      // Récupérer les paramètres du contrat pour calculer les frais mandat
      const contrat = await this.getContratClient(clientId);
      const volumeHebdo = contrat?.weeklyHours || contrat?.volumeHebdo || 10;
      const fraisMandat = getFraisMandat(volumeHebdo);
      const montantTotal = fraisMandat * calculDetails.totalHeures;

      return {
        clientId,
        periode: { 
          debut: debut, 
          fin: fin,
          libelle: this.formaterPeriode(debut, fin)
        },
        totalHeures: calculDetails.totalHeures,
        nombreRendezVous: calculDetails.nombreRendezVous,
        fraisMandatUnitaire: fraisMandat,
        montantTotal: montantTotal,
        contrat: {
          id: contrat?.id,
          volumeHebdo: volumeHebdo,
          baseRateType: contrat?.baseRateType || 'standard'
        },
        rendezVousDetails: calculDetails.rendezVousDetails,
        dateCalcul: new Date().toISOString()
      };

    } catch (error) {
      console.error('❌ Erreur calcul heures facturables:', error);
      throw new Error(`Échec du calcul des heures facturables: ${error.message}`);
    }
  }

  /**
   * Récupère les rendez-vous d'un client pour une période donnée
   * @param {string} clientId - ID du client
   * @param {Date} periodeDebut - Date de début
   * @param {Date} periodeFin - Date de fin
   * @param {Array} statuts - Statuts à inclure
   * @returns {Array} Liste des rendez-vous
   */
  async getRendezVousClient(clientId, periodeDebut, periodeFin, statuts) {
    try {
      const appointmentsRef = collection(db, 'appointments');
      
      // Utiliser exactement la même méthode que ClientDayView
      const debutLocal = new Date(periodeDebut);
      debutLocal.setHours(0, 0, 0, 0);
      const finLocal = new Date(periodeFin);
      finLocal.setHours(23, 59, 59, 999);
      
      console.log(`🔍 Recherche rendez-vous client ${clientId}`, {
        periodeDebut: debutLocal.toISOString(),
        periodeFin: finLocal.toISOString(),
        statuts,
        debutLocal: debutLocal.toLocaleDateString('fr-FR'),
        finLocal: finLocal.toLocaleDateString('fr-FR')
      });

      // Construire la requête EXACTEMENT comme ClientDayView (avec le champ 'date')
      const q = query(
        appointmentsRef,
        where('clientId', '==', clientId),
        where('date', '>=', debutLocal),
        where('date', '<=', finLocal),
        where('status', 'in', statuts),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const appointments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📅 ${appointments.length} rendez-vous trouvés pour client ${clientId}`, {
        statutsRecherches: statuts,
        rendezVousTrouves: appointments.map(apt => ({
          id: apt.id,
          startTime: apt.startTime,
          endTime: apt.endTime,
          statut: apt.statut,
          clientId: apt.clientId
        }))
      });

      // Si aucun rendez-vous trouvé, vérifier si le client existe dans appointments
      if (appointments.length === 0) {
        console.log(`⚠️ Aucun rendez-vous trouvé pour client ${clientId}. Vérification des données...`);
        
        // Vérifier si le client a des rendez-vous avec d'autres statuts
        const qAll = query(
          appointmentsRef,
          where('clientId', '==', clientId),
          where('date', '>=', debutLocal),
          where('date', '<=', finLocal),
          orderBy('date', 'asc')
        );
        
        try {
          const snapshotAll = await getDocs(qAll);
          const allAppointments = snapshotAll.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          if (allAppointments.length > 0) {
            const statutsPresents = [...new Set(allAppointments.map(apt => apt.statut))];
            console.log(`ℹ️ ${allAppointments.length} rendez-vous trouvés avec d'autres statuts:`, {
              statutsPresents,
              rendezVous: allAppointments.map(apt => ({
                id: apt.id,
                date: apt.date,
                startTime: apt.startTime,
                endTime: apt.endTime,
                statut: apt.statut,
                clientId: apt.clientId
              }))
            });
            
            // Si des rendez-vous existent mais avec d'autres statuts, proposer d'ajouter ces statuts
            console.log(`💡 Conseil: Les rendez-vous existent mais avec les statuts: ${statutsPresents.join(', ')}`);
            console.log(`💡 Essayez d'ajouter ces statuts à la recherche ou changez le statut des rendez-vous vers 'completed' ou 'assigne'`);
            
          } else {
            console.log(`❌ Aucun rendez-vous trouvé pour client ${clientId} dans la période spécifiée`);
            
            // Vérifier si le client a des rendez-vous dans d'autres périodes
            const qAllTime = query(
              appointmentsRef,
              where('clientId', '==', clientId),
              orderBy('date', 'asc')
            );
            
            try {
              const snapshotAllTime = await getDocs(qAllTime);
              const allTimeAppointments = snapshotAllTime.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              
              if (allTimeAppointments.length > 0) {
                const statutsPresents = [...new Set(allTimeAppointments.map(apt => apt.statut))];
                console.log(`📋 ${allTimeAppointments.length} rendez-vous trouvés pour ce client dans toutes les périodes:`, {
                  statutsPresents,
                  periodes: allTimeAppointments.map(apt => ({
                    id: apt.id,
                    date: apt.date,
                    startTime: apt.startTime,
                    statut: apt.statut
                  }))
                });
                
                console.log(`💡 Les rendez-vous existent mais avec les statuts: ${statutsPresents.join(', ')}`);
                console.log(`💡 Pour la facturation, les rendez-vous doivent avoir le statut 'completed' ou 'assigne'`);
                
              } else {
                console.log(`❌ Aucun rendez-vous trouvé pour client ${clientId} dans toute la base de données`);
                
                // Vérifier si le client existe dans la collection clients
                try {
                  const clientDoc = await getDoc(doc(db, 'clients', clientId));
                  if (clientDoc.exists()) {
                    console.log(`✅ Client ${clientId} existe dans la collection clients`);
                  } else {
                    console.log(`❌ Client ${clientId} n'existe pas dans la collection clients`);
                  }
                } catch (clientError) {
                  console.warn('⚠️ Impossible de vérifier le client:', clientError.message);
                }
              }
            } catch (allTimeError) {
              console.warn('⚠️ Impossible de vérifier toutes les périodes:', allTimeError.message);
            }
          }
        } catch (error) {
          console.warn('⚠️ Impossible de vérifier tous les statuts:', error.message);
        }
      }

      return appointments;

    } catch (error) {
      console.error('❌ Erreur récupération rendez-vous:', error);
      if (error.code === 'failed-precondition') {
        console.error('🚨 Index Firestore manquant! Créez l\'index via le lien dans l\'erreur');
      }
      throw error;
    }
  }

  /**
   * Calcule la durée en heures entre deux horaires
   * @param {string} heureDebut - Heure de début (format 'HH:MM')
   * @param {string} heureFin - Heure de fin (format 'HH:MM')
   * @returns {number} Durée en heures (arrondie à 2 décimales)
   */
  calculerDureeHeures(heureDebut, heureFin) {
    try {
      console.log(`🕐 Calcul durée entre ${heureDebut} et ${heureFin}`);
      
      // Vérifier si ce sont des horaires simples (format 'HH:MM')
      if (typeof heureDebut === 'string' && typeof heureFin === 'string' && 
          heureDebut.includes(':') && heureFin.includes(':')) {
        
        const [heuresDebut, minutesDebut] = heureDebut.split(':').map(Number);
        const [heuresFin, minutesFin] = heureFin.split(':').map(Number);
        
        // Calculer la durée en heures
        const totalMinutesDebut = heuresDebut * 60 + minutesDebut;
        const totalMinutesFin = heuresFin * 60 + minutesFin;
        const dureeMinutes = totalMinutesFin - totalMinutesDebut;
        const dureeHeures = dureeMinutes / 60;
        
        console.log(`📊 Durée calculée: ${dureeHeures}h (${dureeMinutes} minutes)`);
        
        return Math.round(dureeHeures * 100) / 100; // Arrondi à 2 décimales
      }
      
      // Fallback pour les dates complètes (ancienne méthode)
      const debut = typeof heureDebut === 'string' ? new Date(heureDebut) : heureDebut;
      const fin = typeof heureFin === 'string' ? new Date(heureFin) : heureFin;
      
      if (!(debut instanceof Date) || !(fin instanceof Date)) {
        console.warn('⚠️ Horaires invalides pour calcul durée:', { heureDebut, heureFin });
        return 0;
      }

      const dureeMs = fin.getTime() - debut.getTime();
      const dureeHeures = dureeMs / (1000 * 60 * 60);
      
      return Math.round(dureeHeures * 100) / 100;

    } catch (error) {
      console.error('❌ Erreur calcul durée:', error);
      return 0;
    }
  }

  /**
   * Récupère les paramètres du contrat d'un client
   * @param {string} clientId - ID du client
   * @returns {Object} Paramètres du contrat
   */
  async getContratClient(clientId) {
    try {
      console.log(`🔍 Recherche contrat pour client ${clientId}`);
      
      // Rechercher dans la collection 'contracts' avec le clientId comme ID du document
      try {
        const contratRef = doc(db, 'contracts', clientId);
        const contratDoc = await getDoc(contratRef);
        
        if (contratDoc.exists()) {
          const contrat = contratDoc.data();
          console.log(`📄 Contrat trouvé pour client ${clientId}:`, {
            contractType: contrat.contractType,
            weeklyHours: contrat.weeklyHours,
            baseRateType: contrat.baseRateType
          });
          
          // Structure standardisée selon useClientContract
          return {
            id: contratDoc.id,
            clientId: clientId,
            contractType: contrat.contractType || 'prestation',
            weeklyHours: Number(contrat.weeklyHours) || 0,
            baseRateType: contrat.baseRateType || 'standard',
            majorations: contrat.majorations || {},
            dateCreation: contrat.dateCreation || new Date().toISOString()
          };
        }
      } catch (error) {
        console.warn(`⚠️ Contrat direct non trouvé pour ${clientId}:`, error.message);
      }

      // Rechercher alternative dans la collection 'contracts' avec query
      try {
        const contratRef = collection(db, 'contracts');
        const q = query(contratRef, where('clientId', '==', clientId));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const contrat = snapshot.docs[0].data();
          console.log(`📄 Contrat trouvé via query pour client ${clientId}:`, {
            contractType: contrat.contractType,
            weeklyHours: contrat.weeklyHours,
            baseRateType: contrat.baseRateType
          });
          
          return {
            id: snapshot.docs[0].id,
            clientId: clientId,
            contractType: contrat.contractType || 'prestation',
            weeklyHours: Number(contrat.weeklyHours) || 0,
            baseRateType: contrat.baseRateType || 'standard',
            majorations: contrat.majorations || {},
            dateCreation: contrat.dateCreation || new Date().toISOString()
          };
        }
      } catch (error) {
        console.warn(`⚠️ Recherche contrat via query échouée:`, error.message);
      }

      // Fallback: utiliser des valeurs par défaut
      console.warn(`⚠️ Aucun contrat trouvé pour client ${clientId}, utilisation valeurs par défaut`);
      return {
        id: null,
        clientId: clientId,
        contractType: 'prestation',
        weeklyHours: 10,
        baseRateType: 'standard',
        majorations: {},
        dateCreation: new Date().toISOString(),
        estNouveau: true
      };

    } catch (error) {
      console.error('❌ Erreur récupération contrat:', error);
      throw error;
    }
  }

  /**
   * Formate une période pour l'affichage
   * @param {Date} debut - Date de début
   * @param {Date} fin - Date de fin
   * @returns {string} Période formatée
   */
  formaterPeriode(debut, fin) {
    const formatOptions = { month: 'long', year: 'numeric' };
    
    if (debut.getMonth() === fin.getMonth() && debut.getFullYear() === fin.getFullYear()) {
      // Même mois
      return debut.toLocaleDateString('fr-FR', formatOptions);
    } else {
      // Période sur plusieurs mois
      return `${debut.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })} - ${fin.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}`;
    }
  }

  /**
   * Convertit une valeur en objet Date valide
   * @param {Date|string|any} date - Valeur à convertir
   * @returns {Date} Objet Date valide
   */
  convertirEnDate(date) {
    if (date instanceof Date) {
      return date;
    }
    
    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    
    // Fallback: date actuelle
    console.warn(`⚠️ Date invalide: ${date}, utilisation date actuelle`);
    return new Date();
  }

  /**
   * Calcule les périodes de facturation standards
   * @returns {Object} Périodes disponibles
   */
  getPeriodesFacturation() {
    const maintenant = new Date();
    const moisCourant = new Date(maintenant.getFullYear(), maintenant.getMonth(), 1);
    const moisPrecedent = new Date(maintenant.getFullYear(), maintenant.getMonth() - 1, 1);
    const finMoisPrecedent = new Date(maintenant.getFullYear(), maintenant.getMonth(), 0);
    
    return {
      moisCourant: {
        libelle: 'Mois en cours',
        debut: moisCourant,
        fin: new Date(maintenant.getFullYear(), maintenant.getMonth() + 1, 0)
      },
      moisPrecedent: {
        libelle: 'Mois précédent',
        debut: moisPrecedent,
        fin: finMoisPrecedent
      },
      trimestreCourant: {
        libelle: 'Trimestre en cours',
        debut: new Date(maintenant.getFullYear(), Math.floor(maintenant.getMonth() / 3) * 3, 1),
        fin: new Date(maintenant.getFullYear(), Math.floor(maintenant.getMonth() / 3) * 3 + 3, 0)
      }
    };
  }

  /**
   * Valide si un calcul de facturation est possible
   * @param {string} clientId - ID du client
   * @param {Date} periodeDebut - Date de début
   * @param {Date} periodeFin - Date de fin
   * @returns {Object} Résultat de validation
   */
  async validerCalculFacturation(clientId, periodeDebut, periodeFin) {
    const erreurs = [];

    // Vérifier que le client existe
    try {
      const clientRef = doc(db, 'clients', clientId);
      const clientDoc = await getDoc(clientRef);
      if (!clientDoc.exists()) {
        erreurs.push('Client non trouvé');
      }
    } catch (error) {
      erreurs.push('Erreur vérification client');
    }

    // Vérifier les dates
    if (!periodeDebut || !periodeFin) {
      erreurs.push('Période invalide');
    } else if (periodeDebut >= periodeFin) {
      erreurs.push('La date de début doit être avant la date de fin');
    }

    // Vérifier que la période n'est pas trop longue (max 3 mois)
    const dureeJours = (periodeFin - periodeDebut) / (1000 * 60 * 60 * 24);
    if (dureeJours > 93) {
      erreurs.push('La période ne peut pas dépasser 3 mois');
    }

    return {
      valide: erreurs.length === 0,
      erreurs,
      periodeJours: dureeJours
    };
  }
}

// Export d'une instance unique
export default new BillingCalculationService();
